import React, { useEffect, useState } from "react";
import Modal from "react-modal";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../Button";
import { Loader } from "../Loader";
import { ROUTES } from "../../config/routes";
import { formatCurrency } from "../../helpers/formatNumber";
import { FailedTransactionsAdminService } from "../../api/generated/services/FailedTransactionsAdminService";
import { FailedTransactionViewModel } from "../../api/generated/models/FailedTransactionViewModel";
import { FailedTransactionDetailsViewModel } from "../../api/generated/models/FailedTransactionDetailsViewModel";
import { FailedTransactionSource } from "../../api/generated/models/FailedTransactionSource";
import { ReactComponent as CloseIcon } from "../../assets/icons/x.svg";
import "../../api/openApiConfig";

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div>
    <div className="text-sm text-gray-500">{label}</div>
    <div className="break-all">{children}</div>
  </div>
);

const orDash = (value?: string | number | null) =>
  value === null || value === undefined || value === "" ? "-" : value;

const formatCopenhagen = (iso?: string | null) =>
  iso
    ? new Date(iso).toLocaleString("da-DK", {
        dateStyle: "short",
        timeStyle: "medium",
        timeZone: "Europe/Copenhagen",
      })
    : "-";

const StatusBadge: React.FC<{ status?: string | null }> = ({ status }) => (
  <span
    className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
      status === "Failed"
        ? "bg-red-100 text-red-800"
        : status === "Resolved"
        ? "bg-green-100 text-green-800"
        : "bg-gray-100 text-gray-800"
    }`}
  >
    {orDash(status)}
  </span>
);

const tryPrettyJson = (raw?: string | null): string | null => {
  if (!raw) return null;
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
};

interface FailedTransactionDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  transaction: FailedTransactionViewModel | null;
}

const FailedTransactionDetailsModal: React.FC<FailedTransactionDetailsModalProps> = ({
  isOpen,
  onClose,
  transaction,
}) => {
  const navigate = useNavigate();
  const identityId = transaction?.identityId;

  const goToUser = () => {
    if (!identityId) return;
    navigate(`${ROUTES.ADMIN_USERS()}?search=${encodeURIComponent(identityId)}`);
  };

  const [details, setDetails] = useState<FailedTransactionDetailsViewModel | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || !transaction?.id) return;
    let ignore = false;
    const run = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await FailedTransactionsAdminService.postApiFailedTransactionsAdminGet({
          failedTransactionId: transaction.id,
        });
        if (!ignore) setDetails(response);
      } catch (err) {
        if (!ignore) setError("Failed to load details");
        console.error("Error fetching failed transaction details:", err);
      } finally {
        if (!ignore) setLoading(false);
      }
    };
    run();
    return () => {
      ignore = true;
    };
  }, [isOpen, transaction?.id]);

  const detailsJson = tryPrettyJson(details?.detailsJson);
  const isVisa = transaction?.source === FailedTransactionSource.VISA;

  return (
    <Modal
      className="modal-content md:max-w-[900px]"
      overlayClassName="modal-overlay"
      isOpen={isOpen}
      onRequestClose={onClose}
      contentLabel="Failed Transaction Details"
    >
      <div className="flex justify-between items-start mb-6">
        <h2 className="text-primary font-medium text-xl">Failed Transaction Details</h2>
        <Button onClick={onClose} variant="icon">
          <CloseIcon className="w-3 h-3" />
        </Button>
      </div>

      {transaction && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pb-4 border-b border-gray-200">
            <Field label="Source">{orDash(transaction.source)}</Field>
            <Field label="Status">
              <StatusBadge status={transaction.status} />
            </Field>
            <Field label="External ID">
              <span className="font-mono text-sm">{orDash(transaction.externalId)}</span>
            </Field>
            <Field label="Identity ID">
              <span className="font-mono text-sm">{orDash(transaction.identityId)}</span>
            </Field>
            <Field label="Error Code">{orDash(transaction.errorCode)}</Field>
            <Field label="Failure Count">{orDash(transaction.failureCount)}</Field>
            <Field label="Failed At (Copenhagen)">
              {formatCopenhagen(transaction.failedAtUtc)}
            </Field>
            <Field label="Transaction Time (Copenhagen)">
              {formatCopenhagen(transaction.transactionTimestampUtc)}
            </Field>
            <Field label="Merchant">
              {orDash(transaction.merchantName || transaction.contentfulBusinessId)}
            </Field>
            <Field label="Merchant CVR">{orDash(transaction.merchantCvr)}</Field>
            <Field label="Amount">
              {transaction.amount !== null && transaction.amount !== undefined
                ? `${formatCurrency(transaction.amount)} ${transaction.currency ?? ""}`
                : "-"}
            </Field>
          </div>

          <div>
            <div className="text-sm text-gray-500 mb-1">Error Message</div>
            <div className="text-sm bg-red-50 border border-red-200 p-3 rounded-lg break-all">
              {orDash(transaction.errorMessage)}
            </div>
          </div>

          {isVisa && transaction.externalId && (
            <div>
              <Link
                className="text-primary underline text-sm"
                to={`${ROUTES.ADMIN_VISA_TRANSACTION()}?uniqueTransactionId=${encodeURIComponent(
                  transaction.externalId
                )}`}
              >
                Open full Visa transaction history
              </Link>
            </div>
          )}

          <div className="pt-4 border-t border-gray-200">
            <h3 className="text-sm font-medium text-gray-900 mb-3">
              {isVisa ? "Visa Details" : "Storebox Details"}
            </h3>
            {loading ? (
              <div className="flex justify-center py-4">
                <Loader />
              </div>
            ) : error ? (
              <div className="text-red-500 text-sm bg-red-50 border border-red-200 p-3 rounded-lg">
                {error}
              </div>
            ) : detailsJson ? (
              <pre className="text-xs bg-gray-50 border border-gray-200 rounded-lg p-3 overflow-x-auto max-h-[300px]">
                {detailsJson}
              </pre>
            ) : (
              <div className="text-gray-500 text-sm">No additional details.</div>
            )}
          </div>
        </div>
      )}

      <div className="flex justify-between mt-6 pt-4 border-t border-gray-200">
        <Button
          variant="outline"
          disabled={!identityId}
          title={identityId ? undefined : "No user linked to this transaction"}
          onClick={goToUser}
        >
          User Details
        </Button>
        <Button variant="outline" onClick={onClose}>
          Close
        </Button>
      </div>
    </Modal>
  );
};

export default FailedTransactionDetailsModal;
